import React, { useState, useEffect } from "react";
import { langs } from "../../../sharedJs__generated/langsList";
import { VoicePlatform, VoiceType } from "hooks/__generated__/useTtsVoicesQuery.graphql";
import { useEnvironment } from "../../../graphql/environment";
import { fetchTts } from "../../../queries/tts/fetchTts";
import { getTtsCostPer1kChars, calculateTtsCost } from "./ttsPricing";
import { HeaderLayout } from "components/header/Header";
import useTtsVoices from "hooks/useTtsVoices";

type TtsResult = {
  platform: VoicePlatform;
  voiceId: string;
  voiceName: string;
  audioUrl: string;
  duration: number;
  error?: string;
  type: VoiceType;
};

type Voice = {
  gender: string;
  id: string;
  name: string;
  platform: VoicePlatform;
  type: string;
};

export const TtsTest: React.FC = () => {
  const [text, setText] = useState("Hello, this is a test of text to speech!");
  const [lang, setLang] = useState("en");
  const [speakingRate, setSpeakingRate] = useState(1.0);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<TtsResult[]>([]);
  const [selectedVoices, setSelectedVoices] = useState<Partial<Record<VoicePlatform, string[]>>>({
    azure: [],
    google: []
  });
  const environment = useEnvironment();

  const { data: voicesData } = useTtsVoices({ lang });

  // Add Azure multilingual voice
  const azureMultilingualVoice: Voice = {
    id: "en-US-RyanMultilingualNeural",
    name: "Ryan (Multilingual)",
    gender: "Male",
    platform: "azure",
    type: "Neural"
  };

  // Preselect first voice for each platform when voices data is loaded
  useEffect(() => {
    if (voicesData?.ttsVoices?.voices) {
      const platformVoices = voicesData.ttsVoices.voices.reduce(
        (acc, voice) => {
          const platform = voice.platform;
          if (!acc[platform]) {
            acc[platform] = [];
          }
          acc[platform].push(voice as Voice);
          return acc;
        },
        {} as Record<VoicePlatform, Voice[]>
      );

      const newSelectedVoices: Partial<Record<VoicePlatform, string[]>> = {
        openai: []
      };

      // Preselect first voice for other platforms
      ["azure", "google"].forEach((platform) => {
        const voices = platformVoices[platform as VoicePlatform];
        if (voices?.length) {
          newSelectedVoices[platform as VoicePlatform] = [voices[0].id];
        }
      });

      setSelectedVoices(newSelectedVoices);
    }
  }, [voicesData]);

  const handleVoiceSelect = (platform: VoicePlatform, selectedOptions: string[]) => {
    setSelectedVoices((prev) => ({
      ...prev,
      [platform]: selectedOptions
    }));
  };

  const handleTest = async () => {
    setIsLoading(true);
    setResults([]);

    if (!voicesData?.ttsVoices?.voices) {
      setIsLoading(false);
      return;
    }

    const platformVoices = voicesData.ttsVoices.voices.reduce(
      (acc, voice) => {
        const platform = voice.platform;
        if (!acc[platform]) {
          acc[platform] = [];
        }
        acc[platform].push(voice as Voice);
        return acc;
      },
      {} as Record<VoicePlatform, Voice[]>
    );

    const newResults: TtsResult[] = [];

    // Handle OpenAI voices
    const openAiVoices = selectedVoices.openai || [];
    for (const voiceId of openAiVoices) {
      let startTime = performance.now();
      try {
        const data = await fetchTts(environment, {
          text,
          lang,
          speakingRate,
          voiceId,
          useCache: false
        });

        if (!data?.speech) {
          throw new Error("No speech data received");
        }

        // Convert base64 to Uint8Array
        const binaryString = window.atob(data.speech);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const audioBlob = new Blob([bytes], { type: "audio/mp3" });
        const url = URL.createObjectURL(audioBlob);
        const duration = performance.now() - startTime;

        newResults.push({
          platform: "openai",
          voiceId,
          voiceName: voiceId,
          audioUrl: url,
          duration,
          type: "Neural" as VoiceType
        });
      } catch (error) {
        const duration = performance.now() - startTime;
        newResults.push({
          platform: "openai",
          voiceId,
          voiceName: voiceId,
          audioUrl: "",
          duration,
          error: error instanceof Error ? error.message : "Unknown error",
          type: "Neural" as VoiceType
        });
      }
    }

    // Handle Azure and Google
    for (const platform of ["azure", "google"] as const) {
      const voices =
        platform === "azure" ? [...(platformVoices[platform] || []), azureMultilingualVoice] : platformVoices[platform];
      if (!voices?.length) continue;

      const selectedVoiceIds = selectedVoices[platform] || [];
      for (const voiceId of selectedVoiceIds) {
        const voice = voices.find((v) => v.id === voiceId);
        if (!voice) continue;

        const startTime = performance.now();
        try {
          const data = await fetchTts(environment, {
            text,
            lang: lang,
            speakingRate,
            voiceId: voice.id,
            useCache: false
          });

          if (!data?.speech) {
            throw new Error("No speech data received");
          }

          // Convert base64 to Uint8Array
          const binaryString = window.atob(data.speech);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          const audioBlob = new Blob([bytes], { type: "audio/mp3" });
          const url = URL.createObjectURL(audioBlob);
          const duration = performance.now() - startTime;

          newResults.push({
            platform,
            voiceId: voice.id,
            voiceName: voice.name,
            audioUrl: url,
            duration,
            type: voice.type as VoiceType
          });
        } catch (error) {
          const duration = performance.now() - startTime;
          newResults.push({
            platform,
            voiceId: voice.id,
            voiceName: voice.name,
            audioUrl: "",
            duration,
            error: error instanceof Error ? error.message : "Unknown error",
            type: voice.type as VoiceType
          });
        }
      }
    }

    setResults(newResults);
    setIsLoading(false);
  };

  useEffect(() => {
    return () => {
      // Cleanup audio URLs
      results.forEach((result) => {
        if (result.audioUrl) {
          URL.revokeObjectURL(result.audioUrl);
        }
      });
    };
  }, [results]);

  return (
    <HeaderLayout title="TTS Test" style={{ textAlign: "center" }}>
      <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
        <h1>TTS Test Page</h1>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px" }}>
            Text to speak:
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              style={{ width: "100%", minHeight: "100px", marginTop: "5px" }}
            />
          </label>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px" }}>
            Language:
            <select value={lang} onChange={(e) => setLang(e.target.value)} style={{ width: "100%", marginTop: "5px" }}>
              {Object.entries(langs).map(([code, lang]) => (
                <option key={code} value={code}>
                  {lang.label} ({lang.native})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", marginBottom: "10px" }}>
            Speaking Rate (not working for OpenAI):
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={speakingRate}
              onChange={(e) => setSpeakingRate(parseFloat(e.target.value))}
              style={{ width: "100%", marginTop: "5px" }}
            />
            <span style={{ display: "block", textAlign: "center" }}>{speakingRate}</span>
          </label>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h3>Select Voices</h3>
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Azure and Google voice selections */}
            {voicesData?.ttsVoices?.voices &&
              ["azure", "google"].map((platform) => {
                const platformVoices =
                  platform === "azure"
                    ? [...voicesData.ttsVoices.voices.filter((v) => v.platform === platform), azureMultilingualVoice]
                    : voicesData.ttsVoices.voices.filter((v) => v.platform === platform);
                if (!platformVoices.length) return null;

                return (
                  <div key={platform}>
                    <h4 style={{ margin: "0 0 10px 0", fontSize: "16px", textTransform: "capitalize" }}>
                      {platform} Voices:
                    </h4>
                    <div style={{ display: "grid", gap: "8px" }}>
                      {platformVoices.map((voice) => (
                        <label
                          key={voice.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px",
                            border: "1px solid #eee",
                            borderRadius: "4px",
                            cursor: "pointer"
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={(selectedVoices[platform as VoicePlatform] || []).includes(voice.id)}
                            onChange={(e) => {
                              const currentSelected = selectedVoices[platform as VoicePlatform] || [];
                              handleVoiceSelect(
                                platform as VoicePlatform,
                                e.target.checked
                                  ? [...currentSelected, voice.id]
                                  : currentSelected.filter((v) => v !== voice.id)
                              );
                            }}
                          />
                          <span>
                            {voice.name} ({voice.type})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        <button
          onClick={handleTest}
          disabled={isLoading}
          style={{
            padding: "10px 20px",
            fontSize: "16px",
            cursor: isLoading ? "not-allowed" : "pointer",
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? "Generating..." : "Test TTS"}
        </button>

        {results.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <h2>Results</h2>
            <div style={{ display: "grid", gap: "20px" }}>
              {results.map((result) => (
                <div
                  key={`${result.platform}-${result.voiceId}`}
                  style={{
                    padding: "15px",
                    border: "1px solid #ccc",
                    borderRadius: "4px"
                  }}
                >
                  <h3 style={{ margin: "0 0 10px 0" }}>
                    {result.platform} - {result.voiceName}
                  </h3>
                  <p style={{ margin: "0 0 10px 0", color: result.error ? "red" : "inherit" }}>
                    {result.error || `Duration: ${result.duration.toFixed(2)}ms`}
                  </p>
                  <p style={{ margin: "0 0 10px 0", color: "#666" }}>
                    Cost: ${calculateTtsCost(text, result.platform, result.type as VoiceType).toFixed(3)} ($
                    {getTtsCostPer1kChars(result.platform, result.type as VoiceType).toFixed(3)}/1k chars)
                  </p>
                  {result.audioUrl && <audio controls src={result.audioUrl} style={{ width: "100%" }} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </HeaderLayout>
  );
};
